import { useMemo } from 'react'
import { useHubData } from './useHubData'
import { useExchangeHubStatus } from './useExchangeHubStatus'

export type HubType = 'sales' | 'escrow' | 'purchase' | 'identification' | 'disbursement' | 'irs'

export interface HubAccess {
  canAccess: boolean
  isComplete: boolean
  completionPercent: number
  isSubmitted: boolean
  message?: string
}

export const useHubAccess = (hubType: HubType, user?: { firstName?: string; lastName?: string; address?: { street?: string; city?: string; state?: string; zipCode?: string }; ssn?: string; id?: string; isIdentityVerified?: boolean } | null, isIdentityVerified?: boolean) => {
  const salesData = useHubData('phase1', user?.id)
  const purchaseData = useHubData('acquisition', user?.id)
  const exchangeData = useHubData('exchange', user?.id)
  const { isHubSubmitted, status } = useExchangeHubStatus()

  const hubAccess = useMemo<HubAccess>(() => {
    const demoUnlockAll = typeof window !== 'undefined' && localStorage.getItem('demo_unlock_all') === 'true'

    const isSalesComplete = () => {
      if (isHubSubmitted('sales')) return true
      const data = salesData.data
      return !!(
        data?.exchangerPhaseOne?.names &&
        data?.exchangerPhaseOne?.homeAddress &&
        data?.exchangerPhaseOne?.taxIdNumber &&
        data?.buyer?.names &&
        data?.closingCompanySale?.name &&
        data?.closingCompanySale?.address &&
        data?.relinquished?.addressOrLegalDescription
      )
    }

    const isPurchaseComplete = () => {
      if (isHubSubmitted('purchase')) return true
      const data = purchaseData.data
      return !!(
        data?.exchangerPhaseTwo?.names &&
        data?.seller?.names &&
        data?.closingCompanyPurchase?.name &&
        data?.closingCompanyPurchase?.address &&
        data?.replacement?.addressOrLegalDescription
      )
    }

    const isIdentificationComplete = () => {
      if (isHubSubmitted('identification')) return true
      const identFormSigned = typeof window !== 'undefined' && (
        localStorage.getItem('identification_form_signed') === 'true' ||
        status.documentSignatures?.['identification_form']?.signed === true
      )
      if (identFormSigned) return true
      const exchangeDataVal = exchangeData.data
      const salesDataVal = salesData.data
      const idProperties =
        exchangeDataVal?.identificationForm?.identifiedProperties ||
        (exchangeDataVal as any)?.identificationProperties ||
        salesDataVal?.identificationForm?.identifiedProperties ||
        (salesDataVal as any)?.identificationProperties ||
        []
      const idRule =
        exchangeDataVal?.identificationForm?.idRule ||
        salesDataVal?.identificationForm?.idRule
      const relinquishedPropertyAddress =
        exchangeDataVal?.identificationForm?.relinquishedPropertyAddress ||
        salesDataVal?.identificationForm?.relinquishedPropertyAddress
      const exchangerNames =
        exchangeDataVal?.identificationForm?.exchangerNames ||
        salesDataVal?.identificationForm?.exchangerNames
      return !!(
        exchangerNames &&
        relinquishedPropertyAddress &&
        idRule &&
        (idProperties as unknown[]).length > 0
      )
    }

    const isEscrowComplete = () => isHubSubmitted('escrow')

    const isDisbursementComplete = () => {
      if (isHubSubmitted('disbursement')) return true
      const data = purchaseData.data
      return !!(
        data?.disbursementForm?.exchangerNames &&
        data?.disbursementForm?.amount &&
        data?.disbursementForm?.wireRecipient &&
        data?.disbursementForm?.wireRecipientAddress &&
        data?.disbursementForm?.wireRecipientBankName &&
        data?.disbursementForm?.wireRecipientBankABA &&
        data?.disbursementForm?.wireRecipientBankAccount
      )
    }

    const getCompletionPercent = (hubType: HubType): number => {
      switch (hubType) {
        case 'sales': {
          if (isHubSubmitted('sales')) return 100
          const data = salesData.data
          const exchangeInfoComplete = !!(
            data?.exchangeType &&
            data?.propertySaleStatus &&
            data?.listPrice &&
            data?.mortgagesPaidOff
          )
          const spAgreementUploaded = !!(data?.salesPurchaseAgreementUploaded)
          const bothDocsUploaded = spAgreementUploaded && !!(data?.preliminaryTitleReportUploaded)
          const exchangerFilled = bothDocsUploaded && !!(data?.exchangerPhaseOne?.names && data?.exchangerPhaseOne?.taxIdNumber)
          const propertyDetailsFilled = bothDocsUploaded && !!(
            data?.relinquished?.addressOrLegalDescription &&
            data?.buyer?.names &&
            data?.closingCompanySale?.name
          )
          const salesDocuments: Array<{ name: string; uploaded: boolean; signed?: boolean }> =
            data?.documents || []
          const allSigned =
            salesDocuments.length > 0 &&
            salesDocuments.some(doc => doc.signed === true)
          const completedSteps = [
            exchangeInfoComplete,
            spAgreementUploaded,
            exchangerFilled,
            propertyDetailsFilled,
            allSigned,
          ].filter(Boolean).length
          return Math.round((completedSteps / 5) * 100)
        }

        case 'purchase': {
          if (isHubSubmitted('purchase')) return 100
          if (!user?.id) return 0
          const purchaseFormTabs = ['exchanger', 'purchase-agreement', 'seller', 'closing-company', 'property']
          const tabConfirmedFor = (tab: string): boolean =>
            typeof window !== 'undefined' &&
            localStorage.getItem(`purchase_tab_${tab}_confirmed_${user.id}`) === 'true'
          const allFormTabsConfirmed = purchaseFormTabs.every(tabConfirmedFor)
          const documentsTabConfirmed = tabConfirmedFor('documents')
          const allTabsConfirmed = allFormTabsConfirmed && documentsTabConfirmed
          const conditions = [
            ...purchaseFormTabs.map(tabConfirmedFor),
            allTabsConfirmed,
            documentsTabConfirmed,
          ]
          return Math.round((conditions.filter(Boolean).length / 7) * 100)
        }

        case 'identification': {
          if (isHubSubmitted('identification')) return 100
          const idPropertiesForPercent =
            exchangeData.data?.identificationForm?.identifiedProperties ||
            (exchangeData.data as any)?.identificationProperties ||
            salesData.data?.identificationForm?.identifiedProperties ||
            (salesData.data as any)?.identificationProperties ||
            []
          const identFormSignedForPercent = typeof window !== 'undefined' && (
            localStorage.getItem('identification_form_signed') === 'true' ||
            status.documentSignatures?.['identification_form']?.signed === true
          )
          const relinquishedSaleDateForPercent = typeof window !== 'undefined'
            ? localStorage.getItem('property_sale_date')
            : null
          const idFields = [
            exchangeData.data?.identificationForm?.exchangerNames || salesData.data?.identificationForm?.exchangerNames,
            exchangeData.data?.identificationForm?.relinquishedPropertyAddress || salesData.data?.identificationForm?.relinquishedPropertyAddress,
            exchangeData.data?.identificationForm?.idRule || salesData.data?.identificationForm?.idRule,
            (idPropertiesForPercent as unknown[]).length > 0,
            relinquishedSaleDateForPercent,
            identFormSignedForPercent,
          ]
          const idFilled = idFields.filter(Boolean).length
          return Math.round((idFilled / idFields.length) * 100)
        }

        case 'disbursement':
          if (isHubSubmitted('disbursement')) return 100
          const disbFields = [
            purchaseData.data?.disbursementForm?.exchangerNames,
            purchaseData.data?.disbursementForm?.amount,
            purchaseData.data?.disbursementForm?.wireRecipient,
            purchaseData.data?.disbursementForm?.wireRecipientAddress,
            purchaseData.data?.disbursementForm?.wireRecipientBankName,
            purchaseData.data?.disbursementForm?.wireRecipientBankABA,
            purchaseData.data?.disbursementForm?.wireRecipientBankAccount
          ]
          const disbFilled = disbFields.filter(Boolean).length
          return Math.round((disbFilled / disbFields.length) * 100)

        default:
          return 0
      }
    }

    if (demoUnlockAll) {
      const irsReviewed = typeof window !== 'undefined' && localStorage.getItem('irs_hub_8824_reviewed') === 'true'
      return {
        canAccess: true,
        isComplete: isHubSubmitted(hubType)
          ? true
          : hubType === 'sales' ? isSalesComplete()
          : hubType === 'identification' ? isIdentificationComplete()
          : hubType === 'escrow' ? isEscrowComplete()
          : hubType === 'purchase' ? isPurchaseComplete()
          : hubType === 'disbursement' ? isDisbursementComplete()
          : hubType === 'irs' ? irsReviewed
          : false,
        completionPercent: 100,
        isSubmitted: isHubSubmitted(hubType),
      }
    }

    switch (hubType) {
      case 'sales':
        const userIsVerified = isIdentityVerified !== undefined ? isIdentityVerified : user?.isIdentityVerified
        return {
          canAccess: userIsVerified === true,
          isComplete: isSalesComplete(),
          completionPercent: userIsVerified === true ? getCompletionPercent('sales') : 0,
          isSubmitted: isHubSubmitted('sales'),
          message: userIsVerified === true ? undefined : 'Please verify your identity first'
        }

      case 'escrow': {
        const salesSubmitted = isHubSubmitted('sales')
        const escrowDone = isEscrowComplete()
        return {
          canAccess: salesSubmitted,
          isComplete: escrowDone,
          completionPercent: escrowDone ? 100 : 0,
          isSubmitted: isHubSubmitted('escrow'),
          message: salesSubmitted ? undefined : 'Please complete the Sales Hub first',
        }
      }

      case 'identification': {
        const salesSubmitted = isHubSubmitted('sales')
        return {
          canAccess: salesSubmitted,
          isComplete: isIdentificationComplete(),
          completionPercent: salesSubmitted ? getCompletionPercent('identification') : 0,
          isSubmitted: isHubSubmitted('identification'),
          message: salesSubmitted ? undefined : 'Please complete the Sales Hub first',
        }
      }

      case 'purchase': {
        const idSubmitted = isHubSubmitted('identification')
        const escrowSubmitted = isHubSubmitted('escrow')
        const bothSubmitted = idSubmitted && escrowSubmitted
        return {
          canAccess: bothSubmitted,
          isComplete: isPurchaseComplete(),
          completionPercent: bothSubmitted ? getCompletionPercent('purchase') : 0,
          isSubmitted: isHubSubmitted('purchase'),
          message: bothSubmitted ? undefined : 'Please complete both the Identification Hub and Escrow Hub first',
        }
      }

      case 'disbursement': {
        const purchaseSubmitted = isHubSubmitted('purchase')
        return {
          canAccess: purchaseSubmitted,
          isComplete: isDisbursementComplete(),
          completionPercent: purchaseSubmitted ? getCompletionPercent('disbursement') : 0,
          isSubmitted: isHubSubmitted('disbursement'),
          message: purchaseSubmitted ? undefined : 'Please complete the Purchase Hub first',
        }
      }

      case 'irs': {
        const disbursementSubmitted = isHubSubmitted('disbursement')
        const irsSubmitted = isHubSubmitted('irs')
        const reviewed = typeof window !== 'undefined' && localStorage.getItem('irs_hub_8824_reviewed') === 'true'
        const complete = irsSubmitted || reviewed
        const completionPercent = irsSubmitted ? 100 : reviewed ? 80 : 0
        return {
          canAccess: disbursementSubmitted,
          isComplete: complete,
          completionPercent: disbursementSubmitted ? completionPercent : 0,
          isSubmitted: irsSubmitted,
          message: disbursementSubmitted ? undefined : 'Please complete the Disbursement Hub first',
        }
      }

      default:
        return {
          canAccess: false,
          isComplete: false,
          completionPercent: 0,
          isSubmitted: false,
        }
    }
  }, [hubType, salesData.data, purchaseData.data, exchangeData.data, user?.id, user?.isIdentityVerified, isIdentityVerified, isHubSubmitted, status.hubSubmissions, status.documentSignatures])

  return hubAccess
}
